/**
 * SubscriptionGuard - React hook + banner component that pre-checks the user's
 * subscription status BEFORE they attempt to publish. Shows a clear paywall
 * message if the user has no active plan / trial exhausted / expired.
 *
 * Usage in a page:
 *   const { canPublish, subscription, banner } = useSubscriptionGuard();
 *   ...
 *   {banner}
 *   <Button disabled={!canPublish}>Publicar</Button>
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertCircle, Zap, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export function useSubscriptionGuard() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const goToPricing = () => {
    if (location.pathname === "/pricing") {
      const el = document.getElementById("pricing-tiers");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/pricing#pricing-tiers");
      setTimeout(() => {
        const el = document.getElementById("pricing-tiers");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/billing/subscription");
      setSub(data);
    } catch {
      setSub({ status: "none" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user?.id]);

  const status = sub?.status || "none";
  const trialRemaining = Math.max(
    0,
    (sub?.trial_product_limit || 10) - (sub?.trial_products_count || 0)
  );

  // Publish allowed when: super-admin OR active plan OR trial with slots left
  const isSuperadmin = !!user?.is_superadmin;
  const canPublish =
    isSuperadmin ||
    status === "active" ||
    (status === "trialing" && trialRemaining > 0);

  const banner = (() => {
    if (loading) return null;
    if (isSuperadmin) return null;  // super-admin bypasses all subscription banners

    if (status === "awaiting_authorization") {
      return (
        <Banner
          tone="blue"
          icon={<Clock className="w-5 h-5" />}
          title="Aguardando confirmação do pagamento"
          message="Estamos confirmando seu pagamento com o Mercado Pago. Isso costuma levar poucos segundos. Se demorar, atualize a página."
          cta="Verificar agora"
          onCta={() => window.location.reload()}
          testId="guard-banner-awaiting"
        />
      );
    }

    if (status === "none") {
      return (
        <Banner
          tone="red"
          icon={<AlertCircle className="w-5 h-5" />}
          title="Assinatura necessária para publicar"
          message="Você ainda não tem um plano ativo. Escolha um plano no Magic SIP para começar (7 dias grátis, cartão obrigatório para validar identidade)."
          cta="Ver planos"
          onCta={goToPricing}
          testId="guard-banner-none"
        />
      );
    }

    if (status === "trialing" && trialRemaining <= 0) {
      return (
        <Banner
          tone="amber"
          icon={<AlertCircle className="w-5 h-5" />}
          title="Limite do trial atingido"
          message={`Você publicou ${sub?.trial_products_count} produtos no trial de 7 dias (limite: ${sub?.trial_product_limit}). Confirme sua assinatura para desbloquear publicações ilimitadas dentro da faixa do plano.`}
          cta="Confirmar assinatura"
          onCta={() => navigate("/pricing")}
          testId="guard-banner-trial-limit"
        />
      );
    }

    if (status === "trialing" && trialRemaining > 0) {
      return (
        <Banner
          tone="blue"
          icon={<Clock className="w-5 h-5" />}
          title={`Trial ativo · ${trialRemaining} publicações restantes`}
          message={`Você está no período de teste gratuito de 7 dias. Pode publicar mais ${trialRemaining} produtos antes do limite (${sub?.trial_products_count}/${sub?.trial_product_limit} usados).`}
          cta="Ver detalhes"
          onCta={() => navigate("/settings")}
          testId="guard-banner-trial-info"
          dismissible
        />
      );
    }

    if (status === "past_due" || status === "cancelled") {
      const label = status === "cancelled" ? "Assinatura cancelada" : "Pagamento pendente";
      return (
        <Banner
          tone="red"
          icon={<AlertCircle className="w-5 h-5" />}
          title={label}
          message="Publicações estão bloqueadas até você renovar. Seus produtos já publicados na Nuvemshop tiveram imagens/descrições substituídas por um placeholder — serão restaurados automaticamente ao renovar."
          cta="Renovar assinatura"
          onCta={goToPricing}
          testId="guard-banner-blocked"
        />
      );
    }

    return null;
  })();

  return { canPublish, subscription: sub, loading, banner, reload: load, isSuperadmin };
}

function Banner({ tone, icon, title, message, cta, onCta, testId, dismissible }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const toneMap = {
    red:   { border: "border-red-200",   bg: "bg-red-50",   iconBg: "bg-red-600",   text: "text-red-800",   msg: "text-red-800/80"   },
    amber: { border: "border-amber-200", bg: "bg-amber-50", iconBg: "bg-amber-500", text: "text-amber-900", msg: "text-amber-900/80" },
    blue:  { border: "border-blue-200",  bg: "bg-blue-50",  iconBg: "bg-blue-600",  text: "text-blue-900",  msg: "text-blue-900/80"  },
  };
  const c = toneMap[tone] || toneMap.red;

  return (
    <div
      className={`border ${c.border} ${c.bg} rounded-md p-4 mb-4 flex items-start gap-3`}
      data-testid={testId}
    >
      <div className={`w-9 h-9 rounded-md flex items-center justify-center text-white flex-shrink-0 ${c.iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${c.text} mb-1`}>{title}</div>
        <p className={`text-xs ${c.msg} leading-relaxed`}>{message}</p>
      </div>
      <div className="flex flex-col gap-1 flex-shrink-0">
        <Button
          onClick={onCta}
          size="sm"
          className="rounded-md brand-primary text-white h-9 px-4"
          data-testid={`${testId}-cta`}
        >
          <Zap className="w-3.5 h-3.5 mr-1.5" /> {cta}
        </Button>
        {dismissible && (
          <button
            onClick={() => setDismissed(true)}
            className="text-[10px] font-mono text-zinc-400 hover:text-zinc-700 uppercase"
            data-testid={`${testId}-dismiss`}
          >
            dispensar
          </button>
        )}
      </div>
    </div>
  );
}
